import { useRef, useState, useEffect, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import { signOut } from 'aws-amplify/auth'
import useOnClickOutside from './useOnClickOutside'
import SwitchBranchDropdown from '../common/SwitchBranchDropdown'
import { COLTENE_BRANCH_UUID, handleUserLogout } from '../../actions/root'
import { useAppContext } from '../../libs/contextLib'
import { Pages } from '../../utils/pages'
import { keyboardEvents } from '../../utils/keyboardEvents'
import { capitalizeWords } from '../../utils/functions'
import {
  hasNotificationsPermission,
  hasStorePermission,
  checkPermission,
  bellNotificationsPermissions,
  myOrganizationPermission,
  hasCorporateAdminModulesPermission,
} from '../../utils/permissionValidation'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import './account-menu.scss'

const AccountMenu = (props) => {
  const { setAuthenticated, supportsColteneStore, setIsLoggingOut } = useAppContext()
  const [colteneBranchId, _, removeColteneBranchId] = useLocalStorage(COLTENE_BRANCH_UUID)
  const { t } = useTranslation()
  const history = useHistory()
  const ref = useRef()
  const [userFirstName, setUserFirstName] = useState(null)
  const [userLastName, setUserLastName] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [userAvatar, setUserAvatar] = useState(null)
  const [userPermissions, setUserPermissions] = useState([])
  const [canSeeExtraSections, setCanSeeExtraSections] = useState(false)

  useLayoutEffect(() => {
    setUserFirstName(props.authedUser?.firstname)
    setUserLastName(props.authedUser?.lastname)
    setUserRole(props.authedUser?.userGroup?.toLowerCase())
    setUserAvatar(props.userAvatar)
  }, [props.authedUser])

  useEffect(() => {
    const permissionArr = props.userPermissions ? (Array.isArray(props.userPermissions) ? props.userPermissions : Object.values(props.userPermissions)) : []
    setUserPermissions(permissionArr)
  }, [props.userPermissions])

  useEffect(() => {
    setCanSeeExtraSections(!hasCorporateAdminModulesPermission(props.userPermissions) || colteneBranchId)
  }, [colteneBranchId, props.userPermissions])

  const handleLogout = async (e) => {
    const { dispatch } = props
    setIsLoggingOut(true)
    signOut().then(() => {
      setAuthenticated(false)
      props.toggleShowOnlineAccess(false)
      props.closeNavDisplay()
      props.disableRemoteSession()
      dispatch(handleUserLogout())
      removeColteneBranchId()
    })
    props.setHideAccountMenu(true)
  }

  useOnClickOutside(ref, () => {
    props.setHideAccountMenu(true)
  })

  return (
    <div ref={ref} className="account-menu-container d-flex flex-column">
      <div className="avatar-wrapper">
        <div className="avatar-img">
          {userAvatar ? (
            <img className="image" src={userAvatar} alt="user-avatar" />
          ) : (
            <div className="avatar-initials">
              <span>{`${userFirstName?.charAt(0)}${userLastName?.charAt(0)}`}</span>
            </div>
          )}
        </div>
        <div className="avatar-data">
          <span className="name">
            {userFirstName || ''} {userLastName || ''}
          </span>
          <span className="role">{t([`register.${userRole?.split('-')[0]}`, userRole?.split('-')[0] || ''])}</span>
        </div>
      </div>
      {hasCorporateAdminModulesPermission(userPermissions) && (
        <>
          {colteneBranchId && (
            <Link
              to={Pages.dashbord.route}
              onClick={(e) => {
                e.preventDefault()

                history.replace(Pages.dashbord.route)
                removeColteneBranchId()

                props.setHideAccountMenu(true)
              }}
            >
              <div className="nav-option-wrapper with-margin-top">{t('back_to_my_dashboard')}</div>
            </Link>
          )}
          <div className={'input-title' + (!colteneBranchId ? ' with-big-margin-top' : '')}>{t('switch_account_view')}</div>
          <SwitchBranchDropdown className="with-side-margins dropdown-width with-margin-bottom" onAfterBranchChange={() => props.setHideAccountMenu(true)} />
          <div className="separator with-margin-top" />
        </>
      )}
      <Link to={Pages.accountDetails.route} onClick={() => props.setHideAccountMenu(true)}>
        <div className="nav-option-wrapper with-margin-top">{t('nav.my_account')}</div>
      </Link>
      {canSeeExtraSections && (
        <>
          {hasNotificationsPermission(userPermissions) && checkPermission(userPermissions, bellNotificationsPermissions) && (
            <Link to={Pages.notifications.route} onClick={() => props.setHideAccountMenu(true)}>
              <div className="nav-option-wrapper">{t('nav.notifications')}</div>
            </Link>
          )}
          {supportsColteneStore && hasStorePermission(userPermissions) && (
            <>
              <Link to={Pages.userSubscriptions.route} onClick={() => props.setHideAccountMenu(true)}>
                <div className="nav-option-wrapper">{capitalizeWords(t('nav.my_subscriptions'))}</div>
              </Link>
              <Link to={Pages.userOrders.route} onClick={() => props.setHideAccountMenu(true)}>
                <div className="nav-option-wrapper">{capitalizeWords(t('nav.my_orders'))}</div>
              </Link>
            </>
          )}
          {checkPermission(userPermissions, myOrganizationPermission) && (
            <Link to={Pages.userCompany.route} onClick={() => props.setHideAccountMenu(true)}>
              <div className="nav-option-wrapper">{t('nav.my_organization')}</div>
            </Link>
          )}
        </>
      )}
      <div className="separator with-margin-top" />
      <div
        tabIndex={0}
        role="button"
        className="nav-option-wrapper with-margin-top"
        onClick={handleLogout}
        onKeyDown={(event) => {
          if (event.key === keyboardEvents.ENTER) {
            handleLogout()
          }
        }}
      >
        <span className="signout-btn">{t('account_context.signout')}</span>
      </div>
    </div>
  )
}

function mapStateToProps({ authedUser, userPermissions }) {
  return {
    authedUser,
    userPermissions,
  }
}

export default connect(mapStateToProps)(AccountMenu)

